import React from "react";

import MuiCheckbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { ArrayHelpers, useField, useFormikContext } from "formik";
import styled from "styled-components/macro";

import { Tooltip } from "./Tooltip";

export const StyledCheckbox = styled(MuiCheckbox)(
  ({ theme, disabled }) => `
  && {
    color: ${disabled ? theme.colors.disabled : theme.colors.primary};
    &.Mui-checked {
      color: ${theme.colors.primary};
    }
  }
`,
);

type LabelPlacement = "end" | "start" | "top" | "bottom";

interface CheckBoxBaseProps {
  name?: string;
  label?: React.ReactNode;
  dataTour?: string;
  tooltip?: string;
  labelPlacement?: LabelPlacement;
  disabled?: boolean;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
  value?: boolean;
}

export const CheckBoxBase = ({
  name = undefined,
  label,
  dataTour = undefined,
  tooltip = undefined,
  labelPlacement = "end",
  disabled = undefined,
  onChange,
  value,
}: CheckBoxBaseProps): React.JSX.Element => {
  const control = (
    <StyledCheckbox
      onChange={onChange}
      checked={value}
      data-tour={dataTour || name}
      disabled={disabled}
    />
  );
  return (
    <>
      <FormControlLabel
        control={control}
        label={label}
        labelPlacement={labelPlacement}
      />
      {tooltip && <Tooltip title={tooltip} />}
    </>
  );
};

interface CheckBoxProps {
  name: string;
  dataTour?: string;
  label?: React.ReactNode;
  tooltip?: string;
  labelPlacement?: LabelPlacement;
  disabled?: boolean;
  trueValue?: unknown;
  falseValue?: unknown;
  mutuallyExclusiveWithName?: string;
  mutuallyExclusiveWithFalseValue?: unknown;
  onChangeExtra?: ((value: unknown) => void) | null;
}

export const CheckBox = ({
  name,
  dataTour = undefined,
  label,
  tooltip = undefined,
  labelPlacement = "end",
  disabled = false,
  trueValue = true,
  falseValue = false,
  mutuallyExclusiveWithName = "",
  mutuallyExclusiveWithFalseValue = false,
  onChangeExtra = null,
}: CheckBoxProps): React.JSX.Element => {
  const [field] = useField(name);

  const { setFieldTouched, setFieldValue } = useFormikContext();
  function onChange() {
    const value = field.value === trueValue ? falseValue : trueValue;
    setFieldValue(name, value);
    setFieldTouched(name);

    if (value && mutuallyExclusiveWithName) {
      setFieldValue(mutuallyExclusiveWithName, mutuallyExclusiveWithFalseValue);
      setFieldTouched(mutuallyExclusiveWithName);
    }

    if (typeof onChangeExtra === "function") {
      onChangeExtra(value);
    }
  }

  // Default to unchecked if we dont have a value, this will keep the component
  // in a controlled state.
  const { value = false } = field;

  return (
    <CheckBoxBase
      name={name}
      label={label}
      tooltip={tooltip}
      labelPlacement={labelPlacement}
      disabled={disabled}
      value={value === trueValue}
      onChange={onChange}
      dataTour={dataTour}
    />
  );
};

function defaultGetIsChecked<T>(value: T, values: T[]): boolean {
  return values.includes(value);
}

interface FieldArrayCheckBoxProps<T> {
  name: string;
  label?: React.ReactNode;
  getIsChecked?: (value: T, values: T[]) => boolean;
  array: T[];
  key?: string | number;
  value: T;
  labelPlacement?: LabelPlacement;
  arrayHelpers: ArrayHelpers;
}

export const FieldArrayCheckBox = <T,>({
  name,
  label,
  getIsChecked = defaultGetIsChecked,
  key,
  array,
  value,
  labelPlacement,
  arrayHelpers,
}: FieldArrayCheckBoxProps<T>): React.JSX.Element => {
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      arrayHelpers.push(value);
    } else {
      arrayHelpers.remove(array.indexOf(value));
    }
  };

  const control = (
    <StyledCheckbox
      name={name}
      key={value || key}
      checked={getIsChecked(value, array)}
      onChange={onChange}
    />
  );
  return (
    <FormControlLabel
      control={control}
      label={label}
      labelPlacement={labelPlacement}
    />
  );
};
